import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import * as Sentry from '@sentry/react';

import App from '@/App';
import { onHandleRejectSentryErrors } from '@utils/handleRejectSentryErrors';

import { ENV } from '@constants';

import '@/index.css';

// Description: Lazy load error handling
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  event.stopPropagation();
  window.location.reload();
});

Sentry.init({
  // Performance Monitoring
  tracesSampleRate: 0, // Off all transaction - can be enabled if we have our own billable account Sentry
  sendDefaultPii: true,
  dsn: ENV.VITE_SENTRY_DSN,
  environment: ENV.VITE_ENVIRONMENT,
  enabled: ENV.MODE === 'production', // comment it for debugging localhost
  beforeSend: onHandleRejectSentryErrors,
  tracePropagationTargets: [
    // add all trace api routes
    'localhost',
    ENV.VITE_BONUS_API_URL,
    ENV.VITE_BONUS_SERVICE_API_URL,
    ENV.VITE_PLAYER_REPORTING_SERVICE_API_URL,
  ],
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      matchRoutes,
      useNavigationType,
      createRoutesFromChildren,
    }),
  ],
  ignoreErrors: [
    'React ErrorBoundary Error: Element type is invalid. Received a promise that resolves to: undefined. Lazy element type must resolve to a class or function.',
    'Error: Element type is invalid. Received a promise that resolves to: undefined.',
    'Element type is invalid. Received a promise that resolves to: undefined. Lazy element type must resolve to a class or function.',
    /Lazy element/iv,
    /can't access property ["']default["'], .*_result is undefined/iv,
  ],
});

const domNode = document.getElementById('root');

createRoot(domNode as HTMLElement, {
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
}).render(
  <StrictMode>
    <App />
  </StrictMode>
);
